import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

declare global {
    interface Window { Pusher: typeof Pusher }
}
window.Pusher = Pusher

let echoInstance: Echo | null = null

// ─── Estado de conexão (para polling adaptativo) ──────────────────────────────

type ConnectionListener = (connected: boolean) => void
const connectionListeners = new Set<ConnectionListener>()
let wsConnected = false

function attachConnectionTracking(echo: Echo): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const conn = (echo as any).connector?.pusher?.connection
    if (!conn) return

    const notify = (state: boolean): void => {
        if (wsConnected === state) return
        wsConnected = state
        connectionListeners.forEach(l => l(state))
    }
    conn.bind('connected', () => notify(true))
    conn.bind('disconnected', () => notify(false))
    conn.bind('error', () => notify(false))
    conn.bind('unavailable', () => notify(false))
}

/** Registra listener para mudanças de conexão. Retorna função para remover. */
export function onEchoConnectionChange(listener: ConnectionListener): () => void {
    connectionListeners.add(listener)
    listener(wsConnected)
    return () => connectionListeners.delete(listener)
}

// ─── Singleton Echo ───────────────────────────────────────────────────────────

export function getEcho(authHeaders?: () => Record<string, string>): Echo | null {
    const key = import.meta.env.VITE_REVERB_APP_KEY
    if (!key) return null

    if (!echoInstance) {
        echoInstance = new Echo({
            broadcaster: 'reverb',
            key,
            // wsHost dinamico (window.location.host) cobre multiplos dominios:
            // zgestao.app, app./pdv./go.zecagestao.com.br - o nginx tem proxy /app/* para o reverb.
            wsHost: import.meta.env.VITE_REVERB_HOST || window.location.host,
            // Em producao usamos a mesma porta do site (443 com forceTLS), pois o nginx
            // faz proxy interno do path /app/* para reverb:8080.
            wsPort: Number(import.meta.env.VITE_REVERB_PORT) || (window.location.protocol === 'https:' ? 443 : 80),
            wssPort: Number(import.meta.env.VITE_REVERB_PORT) || 443,
            forceTLS: import.meta.env.VITE_REVERB_SCHEME
                ? import.meta.env.VITE_REVERB_SCHEME === 'https'
                : window.location.protocol === 'https:',
            enabledTransports: ['ws', 'wss'],
            authEndpoint: `${import.meta.env.VITE_APP_URL ?? ''}/broadcasting/auth`,
            auth: { headers: authHeaders ? authHeaders() : {} },
        })
        attachConnectionTracking(echoInstance)
    }
    return echoInstance
}

export function disconnectEcho(): void {
    echoInstance?.disconnect()
    echoInstance = null
    wsConnected = false
    connectionListeners.forEach(l => l(false))
}
